import {
  PbBranding,
  PbPatchBrandingReq,
  PbUploadBrandingImageReq,
  PbUploadBrandingImageReqImageTypeEnum
} from '@itsrever/rever-api'
import {
  BrandingImage,
  BrandingTheme,
  PendingBrandingImages,
  StoredBrandingImages,
  ThemeColorValue
} from '@/domain/branding'

const IMAGE_TYPES: Record<
  BrandingImage,
  PbUploadBrandingImageReqImageTypeEnum
> = {
  logo: PbUploadBrandingImageReqImageTypeEnum.Logo,
  favicon: PbUploadBrandingImageReqImageTypeEnum.Favicon,
  background: PbUploadBrandingImageReqImageTypeEnum.Background
}

export function toStoredBrandingImages(
  stored: PbBranding
): StoredBrandingImages {
  return {
    logo: stored.logo_url ?? '',
    favicon: stored.favicon_url ?? '',
    background: stored.background_image_url ?? ''
  }
}

export function toBrandingTheme(stored: PbBranding): BrandingTheme {
  return {
    primary: toThemeColor(
      stored.primary_theme_color_main,
      stored.primary_theme_color_light,
      stored.primary_theme_color_dark,
      stored.primary_theme_color_contrast_text
    ),
    borderRadius: stored.border_radius ?? '',
    inputVariant: stored.input_variant ?? '',
    title: stored.title ?? '',
    backgroundTextColor: stored.background_text_color ?? '',
    logoHeightDesktop: stored.logo_height_desktop ?? '',
    logoHeightMobile: stored.logo_height_mobile ?? ''
  }
}

// A colour counts as configured only with all four variants, because that is the rule the portal
// applies: a partial one is ignored there and the default palette wins. Showing it as unset is
// what the portal actually renders.
function toThemeColor(
  main?: string,
  light?: string,
  dark?: string,
  contrastText?: string
): ThemeColorValue | null {
  if (!main || !light || !dark || !contrastText) return null
  return { main, light, dark, contrastText }
}

export interface BrandingSavePlan {
  uploads: PbUploadBrandingImageReq[]
  // Null when nothing but images changed: patchBranding with only an id would be a write of
  // nothing, and the three image urls are refused there in any case.
  patch: PbPatchBrandingReq | null
}

// toBrandingSavePlan decides what a save sends, separately from sending it. Images and attributes
// travel to different endpoints and either side can be empty, so the combinations are worth
// pinning down in a test rather than discovering against a live e-commerce.
export function toBrandingSavePlan(
  ecommerceId: string,
  original: BrandingTheme,
  edited: BrandingTheme,
  images: PendingBrandingImages
): BrandingSavePlan {
  const request = toBrandingPatchRequest(ecommerceId, original, edited)

  return {
    // Built from the entries rather than the keys, so a key present with no image cannot become
    // an upload carrying no content: the endpoint would refuse that as an empty image, which
    // reads to a merchant as a save that broke for no reason.
    uploads: Object.entries(images)
      .filter(([, prepared]) => prepared)
      .map(([image, prepared]) => ({
        ecommerce_id: ecommerceId,
        image_type: IMAGE_TYPES[image as BrandingImage],
        content: prepared!.content
      })),
    patch: hasAttributesToPatch(request) ? request : null
  }
}

// The endpoint writes exactly what it receives, so only the edited attributes travel. A brand
// colour always goes as its four variants: the endpoint refuses a partial one, and the portal
// only applies a colour that has all four.
export function toBrandingPatchRequest(
  ecommerceId: string,
  original: BrandingTheme,
  edited: BrandingTheme
): PbPatchBrandingReq {
  const request: PbPatchBrandingReq = { ecommerce_id: ecommerceId }

  if (colorChanged(original.primary, edited.primary)) {
    request.primary = toPbThemeColor(edited.primary)
  }
  if (original.borderRadius !== edited.borderRadius) {
    request.border_radius = edited.borderRadius
  }
  if (original.inputVariant !== edited.inputVariant) {
    request.input_variant = edited.inputVariant
  }
  if (original.title !== edited.title) {
    request.title = edited.title
  }
  if (original.backgroundTextColor !== edited.backgroundTextColor) {
    request.background_text_color = edited.backgroundTextColor
  }
  if (original.logoHeightDesktop !== edited.logoHeightDesktop) {
    request.logo_height_desktop = edited.logoHeightDesktop
  }
  if (original.logoHeightMobile !== edited.logoHeightMobile) {
    request.logo_height_mobile = edited.logoHeightMobile
  }

  return request
}

// Derived from the patch rather than compared again, so "what changed" and "what travels" cannot
// drift apart as attributes are added.
export function changedBrandingAttributes(
  original: BrandingTheme,
  edited: BrandingTheme
): string[] {
  return Object.keys(toBrandingPatchRequest('', original, edited)).filter(
    (attribute) => attribute !== 'ecommerce_id'
  )
}

function colorChanged(
  original: ThemeColorValue | null,
  edited: ThemeColorValue | null
): boolean {
  if (original === null && edited === null) return false
  if (original === null || edited === null) return true
  return (
    original.main !== edited.main ||
    original.light !== edited.light ||
    original.dark !== edited.dark ||
    original.contrastText !== edited.contrastText
  )
}

// Clearing a colour sends its four variants empty, which is how the portal is put back on the
// default palette.
function toPbThemeColor(color: ThemeColorValue | null) {
  return {
    main: color?.main ?? '',
    light: color?.light ?? '',
    dark: color?.dark ?? '',
    contrast_text: color?.contrastText ?? ''
  }
}

function hasAttributesToPatch(request: PbPatchBrandingReq): boolean {
  return Object.keys(request).some((attribute) => attribute !== 'ecommerce_id')
}
